import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';

type FavoriteAssignmentParams = {
  assignmentId: number;
};

type MutationCallbacks = {
  onSettled?: () => void;
  onError?: () => void;
};

export const useFavoriteAssignmentsMutations = () => {
  const queryClient = useQueryClient();
  const { axios } = useNetworkRequest();

  const markAsFavorite = (id: number) => axios.post(`/v3/professional/assignments/${id}/favorite`);
  const unmarkAsFavorite = (id: number) =>
    axios.delete(`/v3/professional/assignments/${id}/favorite`);

  const syncFavoriteQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['favoriteAssignments'],
      exact: false,
    });

    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'favoriteAssignments',
    });
  };

  const { mutate: markAsFavoriteMutation, isLoading: isMarkingFavorite } = useMutation(
    ({ assignmentId }: FavoriteAssignmentParams) => markAsFavorite(assignmentId),
    {
      onSuccess: syncFavoriteQueries,
      onError: (error) => {
        console.error('Error marking assignment as favorite:', error);
      },
    },
  );

  const { mutate: unmarkAsFavoriteMutation, isLoading: isUnmarkingFavorite } = useMutation(
    ({ assignmentId }: FavoriteAssignmentParams) => unmarkAsFavorite(assignmentId),
    {
      onSuccess: syncFavoriteQueries,
      onError: (error) => {
        console.error('Error unmarking assignment as favorite:', error);
      },
    },
  );

  const markAsFavoriteWithCallbacks = (
    params: FavoriteAssignmentParams,
    callbacks?: MutationCallbacks,
  ) => {
    markAsFavoriteMutation(params, {
      onSettled: callbacks?.onSettled,
      onError: () => {
        callbacks?.onError?.();
      },
    });
  };

  const unmarkAsFavoriteWithCallbacks = (
    params: FavoriteAssignmentParams,
    callbacks?: MutationCallbacks,
  ) => {
    unmarkAsFavoriteMutation(params, {
      onSettled: callbacks?.onSettled,
      onError: () => {
        callbacks?.onError?.();
      },
    });
  };

  return {
    markAsFavoriteMutation: markAsFavoriteWithCallbacks,
    unmarkAsFavoriteMutation: unmarkAsFavoriteWithCallbacks,
    isMarkingFavorite,
    isUnmarkingFavorite,
    isLoading: isMarkingFavorite || isUnmarkingFavorite,
  };
};
